import React, { useState } from 'react';
import Modal from 'react-modal';
import { UnfulfilledReason } from 'globalTypes';

import CurrentUser from 'auth/CurrentUser';
import BoxBody from 'components/BoxBody';
import { ConfirmDeleteDialog } from 'coaching/components/CoachingCoordinatorView/ConfirmDeleteDialog';
import CoachingConstants from 'coaching/CoachingConstants';
import { useCoachingViewState } from 'coaching/components/CoachingView';
import {
  CoachingSessionProps,
  SessionRowHeader,
  SessionRowMetadata,
  useCoachingSessionState,
} from 'coaching/components/CoachingCoordinatorView/SharedCoordinatorSessionRowComponents';
import EventTracking from 'lib/EventTracking';
import Icon, { IconName } from 'components/Icon';
import Link from 'components/Link';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Text from 'components/Text';
import TextBadge from 'components/TextBadge';
import { CoachingInstanceGQL as CoachingInstanceType } from 'coaching/containers/GetCoachingSessionsAndRecordingsContainer';
import { formatDateForCoordinator } from 'lib/dateTimeFormat';
import CoachingCoordinatorTransactionSidebar from 'coaching/components/CoachingCoordinatorView/CoachingCoordinatorTransactionSidebar';

type Props = CoachingSessionProps & {
  isPastEvent: boolean;
};

type SessionActionProps = {
  children: React.ReactElement;
  // It's more intuitive to determine the wrapper action by whether the session is in
  // the future, so we flip the isPastEvent boolean when passing it to this component.
  isUpcoming: boolean;
  // If this is passed, it means the element is associated with a specific instance:
  // either a single instance on a recurring schedule, or a one-off session.
  // If it is NOT passed, then the element represents a recurring session schedule.
  instance?: CoachingInstanceType;
  isRowHeader?: boolean; // used in the test selector
  // used to display the user's name in the transactions sidebar header when passed in
  userName?: string;
  duration?: number;
  participantUuid?: string;
};

// helper functions
const trackEvent = (event: string) => {
  EventTracking.trackKey(
    `${CoachingConstants.COORDINATOR_TOOL_EVENT_PREFIX}.participant_sidebar.${event}`
  );
};

type WrapperAction = 'delete' | 'transactions';

function getSessionAction(isUpcoming: boolean, instance?: CoachingInstanceType) {
  const currentUser = CurrentUser.getUser();
  const hasBTDFeature = currentUser?.hasFeature('billing_transaction_data.web.dec-21');

  if (isUpcoming) return 'delete';
  else if (instance) return hasBTDFeature ? 'transactions' : 'delete';
  else return null;
}

/**
 * We wrap every session schedule and each of their child instances in this action wrapper.
 * Depending on the context, it can do one of three things:
 *
 * 1. Turn the element into a link that opens the transactions sidebar
 * 2. Turn the element into a link that opens a delete modal
 * 3. Nothing
 *
 * The transactions sidebar (1) is for past instances, since we charge per-instance when
 * the instance ends. Thus elements representing individual instances or one-off sessions
 * in the past should link to the sidebar. (From there, we can also delete the instance.)
 *
 * We cannot show the transactions sidebar for future sessions since they have no
 * transactions, but we can delete them (2). Anything in the future can be deleted:
 * recurring session schedules, one-off sessions, or individual instances.
 *
 * We do nothing (3) when the wrapped element represents a recurring session schedule (ie,
 * it is not directly associated with an instance) that is in the past, since we cannot
 * delete a recurring schedule in the past and only instances can have transactions.
 */
const SessionActionWrapper = ({
  children,
  isUpcoming,
  instance,
  isRowHeader,
  userName,
  duration,
  participantUuid,
}: SessionActionProps) => {
  const { setConfirmDeleteDialog } = useCoachingSessionState();
  const { setSidebar } = useCoachingViewState();

  const action = getSessionAction(isUpcoming, instance);
  if (action === null) return children;

  const onClickMapping: { [k in WrapperAction]: () => void } = {
    delete: () => {
      trackEvent(instance ? 'remove_session_instance.click' : 'remove_session.click');
      setConfirmDeleteDialog({
        ongoingSeries: !instance,
        instanceDateString: instance?.startDateTime
          ? formatDateForCoordinator(instance.startDateTime, 'ddd, MMM D')
          : undefined,
        instanceStartDateTime: instance?.startDateTime,
        coachingSessionInstanceUuid: instance?.eventId,
      });
    },
    transactions: () => {
      setSidebar(
        <CoachingCoordinatorTransactionSidebar
          instance={instance!}
          duration={duration}
          userName={userName}
          participantUuid={participantUuid}
        />
      );
    },
  };
  const iconMapping: { [k in WrapperAction]: IconName } = {
    delete: 'x',
    transactions: 'chevron-right',
  };

  return (
    <Link
      data-test={`${action}-action${isRowHeader ? '-rowHeader' : ''}`}
      color="chalkboard"
      noHover
      onClick={onClickMapping[action]}>
      <MediaObject figure="right" center>
        {children}
        <Icon className="CoachingSessionRow-action" name={iconMapping[action]} size="small" />
      </MediaObject>
    </Link>
  );
};

/**
 * Renders a coaching session schedule, either one-off or recurring.
 * If the session is recurring, also renders an expandable list of instances.
 */
export const CoachingSessionRow = ({
  session,
  participantUuid,
  userName,
  userType,
  isPastEvent,
}: Props) => {
  const [pageNumber, setPageNumber] = useState<number>(1);
  const { confirmDeleteDialog } = useCoachingSessionState();

  const { instances, duration } = session;
  const isOneOff = instances.length === 1;
  const oneOffInstance = isOneOff ? instances[0] : undefined;

  const renderInstances = () => {
    if (instances.length < 2) return null;

    const numberOfInstancesToShow = pageNumber * CoachingConstants.COACHING_SESSIONS_PAGE_LENGTH;
    const instancesToShow = instances.slice(0, numberOfInstancesToShow);
    return (
      <Row size="xsmall">
        <BoxBody size="xxsmall">
          {instancesToShow.map(instance => {
            const dateString = formatDateForCoordinator(instance.startDateTime, 'ddd, MMM D');
            return (
              <div
                className="CoachingSessionRow-instance"
                key={instance.eventId}
                data-test="coaching-session-instance">
                <SessionActionWrapper
                  isUpcoming={!isPastEvent}
                  instance={instance}
                  duration={duration}
                  userName={userName}
                  participantUuid={participantUuid}>
                  <MediaObject center gutter="xsmall">
                    <Text element="div">{dateString}</Text>
                    {instance.unfulfilledReason ? (
                      <UnfulfilledReasonBadge reason={instance.unfulfilledReason} />
                    ) : (
                      <span />
                    )}
                  </MediaObject>
                </SessionActionWrapper>
              </div>
            );
          })}
          {pageNumber === 1 && instances.length > CoachingConstants.COACHING_SESSIONS_PAGE_LENGTH && (
            <Text className="CoachingSessionRow-showMore" element="div" weight="semibold">
              <Link
                onClick={() => {
                  trackEvent('session_show_more_instances.click');
                  setPageNumber(2);
                }}>
                Show more
              </Link>
            </Text>
          )}
        </BoxBody>
      </Row>
    );
  };

  return (
    <>
      <Modal data-test="confirm-delete-modal" isOpen={!!confirmDeleteDialog}>
        <ConfirmDeleteDialog {...{ session, participantUuid, userName, userType }} />
      </Modal>
      <div className="CoachingSessionRow">
        <BoxBody size="xxsmall" className="CoachingSessionRow-header">
          <SessionActionWrapper isRowHeader isUpcoming={!isPastEvent} instance={oneOffInstance}>
            <div>
              <SessionRowHeader session={session} />
              <SessionRowMetadata session={session} />
              {oneOffInstance?.unfulfilledReason && (
                // Only rendered if the session is actually a one-off,
                // since oneOffInstance is undefined otherwise.
                <UnfulfilledReasonBadge reason={oneOffInstance.unfulfilledReason} />
              )}
            </div>
          </SessionActionWrapper>
        </BoxBody>
        {renderInstances()}
      </div>
    </>
  );
};

const UnfulfilledReasonBadge = ({ reason }: { reason: UnfulfilledReason }) => {
  return (
    <TextBadge textSize="xxxxsmall" textWeight="normal" color="eraser">
      {formatUnfulfilledReason(reason)}
    </TextBadge>
  );
};

function formatUnfulfilledReason(reason: UnfulfilledReason) {
  switch (reason) {
    case 'override':
      return 'Edited hours';
    case 'coach_paused':
      return 'Coach paused';
    case 'schedule_changed':
      return 'New schedule';
    default:
      return 'Unknown';
  }
}

export default CoachingSessionRow;
